/* eslint-disable max-len */
import React, { useContext, useRef } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  extend,
  string,
  QueryProvider,
  QueryContext,
  params
} from '@thd-nucleus/data-sources';
import { Carousel } from '@one-thd/sui-carousel';
import classNames from 'classnames';
import { Card, useBreakpoint } from '@one-thd/sui-atomic-components';
import { BuyAgainHeader } from './BuyAgainHeader';
import {
  getSkippers,
  isAppliance,
  getLoyaltyMembershipInput,
  getUdsProductBreakPoint,
  hasBadges
} from '../utils/helpers';
import { BuyAgainPod } from './BuyAgainPod';
import { SubscriptionsText } from './SubscriptionsText';
import { udsWrappedProduct } from './dataModel';

const { bool: boolType, any, string: stringType } = proptypes;
// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string()
  })
}, BuyAgainPod);

const BuyAgainCarousel = (props) => {

  const { channel } = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext) || {};
  const isMobile = channel === 'mobile';
  const {
    data,
    storeId,
    brandTitleMaxLine,
    hideATC,
    hideFavorites,
    hideBadge,
    hideRating,
    showSubscribeAndSave,
    membershipInformation,
    zipCode,
    slidesPer,
    noATCFulfillment,
    hideControls,
    analyticsAnchorProductSku,
    scheme,
    parent,
    slidesToShow,
    showViewAll,
    isB2BCustomer,
    hideOutline,
    showHybridCarousel
  } = props;

  const qtxDefaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: getLoyaltyMembershipInput(membershipInformation),
    ...getSkippers({ skipSubscribeAndSave: !showSubscribeAndSave }),
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  const { products } = data || {};
  const { greater, exact } = useBreakpoint('lg') || {};
  const isLargeScreen = greater || exact;
  const shouldShowHybrid = isLargeScreen && showHybridCarousel && !!products?.length && products?.length <= 3;
  const carouselClasses = classNames('sui-w-full', { 'sui-max-w-[45%]': shouldShowHybrid });
  const subscriptionClasses = classNames('sui-overflow-hidden', 'sui-p-2',
    '@3xl:sui-min-w-[40%] @3xl:sui-max-w-[50%]'
  );

  const carouselSwipe = useRef(false);

  const handleSwipe = () => {
    carouselSwipe.current = true;
  };

  const handleNextSwipe = () => {
    if (!carouselSwipe.current) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('buy-again.componentScroll', { direction: 'right' });
    } else {
      carouselSwipe.current = false;
    }
  };

  const handlePrevSwipe = () => {
    if (!carouselSwipe.current) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('buy-again.componentScroll', { direction: 'left' });
    } else {
      carouselSwipe.current = false;
    }
  };

  let breakpoints = ({});
  breakpoints = !shouldShowHybrid ? 
    getUdsProductBreakPoint(slidesToShow, slidesPer) 
    : getUdsProductBreakPoint(null, 3);

  let placeholderTxtLines;
  if (!isMobile) {
    placeholderTxtLines = 7;
  } else {
    placeholderTxtLines = hideATC ? 4 : 6;
  }

  const getScheme = (schemeName) => {
    return (schemeName === 'products_b2b' || schemeName === 'products') ? 'reorder' : schemeName;
  };

  const badgeCheck = hasBadges(products, hideBadge);

  const showViewAllLink = showViewAll === false ? false : (isB2BCustomer || showViewAll);

  return (
    <Card
      data-component="BuyAgain"
      data-testid="buy-again"
      className={classNames('sui-h-full sui-@container', { 'sui-border-0': hideOutline })}
      CardContentProps={{ className: 'sui-flex sui-flex-col sui-w-full sui-h-full sui-gap-4 sui-p-4' }}
    >
      <BuyAgainHeader showViewAllLink={showViewAllLink} />
      <div className="sui-flex sui-flex-row sui-w-full sui-justify-between">
        {products?.length > 0 && (
          <div id="bia-carousel" className={carouselClasses}>
            <QueryProvider
              dataSource="reorder"
              cacheKey="bia-carousel"
              defaultVariables={qtxDefaultVariables}
            >
              <Carousel
                key={shouldShowHybrid ? 'hybrid' : 'full'}
                breakpoints={breakpoints}
                hideControls={hideControls}
                disableMargin
                layout="full-bleed"
                containerId="bia-carousel"
                SwiperProps={{
                  onSlideNextTransitionEnd: handleNextSwipe,
                  onSlidePrevTransitionEnd: handlePrevSwipe,
                  onSliderFirstMove: handleSwipe,
                }}
                type="peek"
                peek="md"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(isMobile ? ({ slidesPerGroup: 2 }) : {})}
              >
                {products.map((dynamicProduct, idx) => {
                  const { product, recentOrderDate } = dynamicProduct;
                  const { itemId } = product;
                  const { preferredPriceFlag } = product?.pricing || {};
                  const { canonicalUrl } = product?.identifiers || {};
                  return (
                    <Wrapper key={idx}>
                      <BuyAgainPod
                        itemId={itemId}
                        key={itemId}
                        position={idx}
                        storeId={storeId}
                        scheme={getScheme(scheme)}
                        anchorProduct={analyticsAnchorProductSku}
                        parent={parent || `recs-product-pod-carousel-${scheme}`}
                        hideFavorites={hideFavorites}
                        hideBadge={badgeCheck}
                        hideATC={hideATC}
                        hideRating={hideRating}
                        preferredPriceFlag={preferredPriceFlag}
                        hideSavingsText={!preferredPriceFlag}
                        brandTitleMaxLine={brandTitleMaxLine}
                        strategy={dynamicProduct?.strategy}
                        noATCFulfillment={isAppliance(product) ? false : noATCFulfillment}
                        url={canonicalUrl}
                        recentOrderDate={recentOrderDate}
                        recsApiName={data.apiName}
                        recsVersion={data.version}
                      />
                    </Wrapper>
                  );
                }
                )}
              </Carousel>
            </QueryProvider>
          </div>
        )}
        {shouldShowHybrid && (
          <SubscriptionsText hideTitle fullWidthButton className={subscriptionClasses} />
        )}
      </div>
    </Card>
  );
};

BuyAgainCarousel.dataModel = extend(Wrapper, udsWrappedProduct);

BuyAgainCarousel.displayName = 'BuyAgainCarousel';

BuyAgainCarousel.propTypes = {
  ...BuyAgainPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  showLoading: boolType,
  storeId: stringType.isRequired,
  subtitle: stringType,
  hideATC: boolType,
  hideFavorites: boolType,
  hideBadge: boolType,
  keepAddToCart: boolType,
  analyticsAnchorProductSku: stringType,
  hideRating: boolType,
  noATCFulfillment: boolType,
  hideControls: boolType,
  hideOutline: boolType,
};

BuyAgainCarousel.defaultProps = {
  hideTitle: false,
  showLoading: true,
  loading: false,
  data: null,
  subtitle: null,
  hideBadge: false,
  hideATC: false,
  hideFavorites: true,
  keepAddToCart: null,
  analyticsAnchorProductSku: '',
  hideRating: false,
  noATCFulfillment: true,
  hideControls: false,
  hideOutline: false,
};

export { BuyAgainCarousel };